import update from 'immutability-helper';
import { capitalize, isEmpty } from 'lodash';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { cancelEnrolment } from '../../../../actions/hub/hubCancelActions';
import { api } from '../../../../api';
import type { Enrolment } from '../../../../api/types/enrolment';
import CalendarIcon from '../../../../assets/images/icon-outline-calendar.svg';
import ErrorBlock from '../../../../common/ErrorBlock';
import CardLayout from '../../../../common/hub/CardLayout';
import useAccountPricingPlanQuery from '../../../../hooks/queries/useAccountPricingPlanQuery';
import { CancelReason } from '../../../../types/hubInterfaces';
import { InitialState, UserPricingPlan } from '../../../../types/hubTypes';
import { DATE_FORMAT_TYPE_8 } from '../../../../util/constants';
import { formatSession } from '../../../../util/helpers';
import SummerPauseAndDiscount from '../../../../v2/features/cancel-enrolment/SummerPauseAndDiscount';
import { isSummerCancelPromoEnabled } from '../../../../v2/features/pause-enrolment/helpers';
import ChangePricingPlanPage from '../../../../views/account/ChangePricingPlanPage';
import { DatePicker, useDatePickerProps } from '../../../DatePicker/DatePicker';
import FormSubHeading from '../../../hub/contact/FormSubHeading';
import { CancellationDeterrent } from '../CancellationDeterrent';
import { CancellationSteps } from '../CancellationSteps';
import { CancelEnrolmentAlert } from './CancelEnrolmentAlert';

interface Props {
  selectedEnrolment: Enrolment & {
    nextSessionDetail?: string;
    nextSessionTutor?: string;
    timezone?: string;
  };
  goBack(): void;
  submitSuccessCallback(): void;
  enrolmentPolicyError?: unknown;
  setCancelRequestedDate(date: string): void;
}

const changePlanWhitelist = [
  'Flexi 23',
  'Flexi 2306',
  'Flexi',
  'Flexi 2312',
  'Saver 24',
  'Saver 2408',
  'Saver 2506',
  'Saver',
  'Saver 2501',
  'Saver Plus 2507',
  'Saver 2507',
];

function shouldSuggestChangePlan(
  selectedReasons: CancelReason[],
  currentPricingPlan: UserPricingPlan,
  upcomingPricingPlan?: UserPricingPlan
) {
  const isChangingToValueMax = upcomingPricingPlan?.pricingPlanDisplayName === 'Value Max';
  return (
    !!currentPricingPlan &&
    !isChangingToValueMax &&
    changePlanWhitelist.includes(currentPricingPlan.pricingPlanName) &&
    selectedReasons.some(
      (reason) =>
        reason.label === "The price doesn't work for us" || reason.label === 'We can no longer afford the service'
    )
  );
}

const shouldOfferSummerPauseAndDiscount = (selectedReasons: CancelReason[]) => {
  return (
    isSummerCancelPromoEnabled() && selectedReasons.some((reason) => reason.label === 'Cancelling for summer holiday')
  );
};

const FormCancelEnrolment = ({
  selectedEnrolment,
  goBack,
  submitSuccessCallback,
  enrolmentPolicyError,
  setCancelRequestedDate,
}: Props) => {
  const { enrolmentSfid, student, subject, tutor } = selectedEnrolment;
  const dispatch = useDispatch();
  const ref = useRef(null);
  const previousRatingRef = useRef(0);
  const { fetchError: submitError, errorMsg } = useSelector((state: InitialState) => state.ui.apiState.hubContactPage);
  const { data: accountPricingPlan } = useAccountPricingPlanQuery();
  const { data: salesforceCases, error: salesforceCasesQueryError } = api.salesforceCase.all.useQuery(enrolmentSfid);
  const { data: accountDetails } = api.account.details.useQuery();

  const hasTakenSummerOffer = salesforceCases?.cases?.length > 0;

  const rawFirstSession = selectedEnrolment.sessions?.[0];
  const sessionForFormat =
    selectedEnrolment.startTime && selectedEnrolment.endTime
      ? {
          startTime: selectedEnrolment.startTime,
          endTime: selectedEnrolment.endTime,
          subject: selectedEnrolment.subject,
          tutor: selectedEnrolment.tutor,
        }
      : rawFirstSession != null
        ? {
            ...rawFirstSession,
            startTime:
              'startTime' in rawFirstSession && rawFirstSession.startTime != null
                ? rawFirstSession.startTime
                : rawFirstSession.start,
            endTime:
              'endTime' in rawFirstSession && rawFirstSession.endTime != null
                ? rawFirstSession.endTime
                : rawFirstSession.end,
            subject: selectedEnrolment.subject,
            tutor: selectedEnrolment.tutor,
          }
        : null;

  const nextSessionWhenAndWhere = sessionForFormat
    ? (() => {
        const { date, time } = formatSession({
          session: sessionForFormat,
          timezone: accountDetails?.timezone ?? selectedEnrolment.timezone ?? 'Australia/Sydney',
          sessionDateFormat: DATE_FORMAT_TYPE_8,
          useSemiColon: false,
        });
        return `${date} at ${time}`;
      })()
    : selectedEnrolment.nextSessionDetail;

  const nextSessionIntro = selectedEnrolment.nextSessionTutor ?? `Next session with ${tutor}`;

  const datePickerProps = useDatePickerProps({
    labelText: 'Select a date to cancel from',
    placeholder: 'Select date',
    customInputClasses: 'w-full rounded-full !pl-3',
    options: {
      enableTime: false,
      minDate: new Date().fp_incr(1),
      altInput: true,
      altFormat: 'j F Y',
      dateFormat: 'Y-m-d',
    },
  });
  const selectedDate =
    Array.isArray(datePickerProps.value) && datePickerProps.value[0] instanceof Date === true
      ? moment(datePickerProps.value[0]).format('YYYY-MM-DD')
      : '';

  const [cancelSelection, setCancelSelection] = useState({
    selectedDate,
    rating: 0,
    selectedReasons: [],
    message: '',
    other: {
      message: '',
    },
  });

  useEffect(() => {
    if (
      (cancelSelection.rating <= 3 && previousRatingRef.current >= 4) ||
      (cancelSelection.rating >= 4 && previousRatingRef.current <= 3)
    ) {
      setCancelSelection((current) =>
        update(current, {
          selectedReasons: {
            $set: [],
          },
        })
      );
    }
  }, [cancelSelection.rating]);
  useEffect(() => {
    previousRatingRef.current = cancelSelection.rating;
  }, [cancelSelection.rating]);
  useEffect(() => {
    setCancelSelection((current) =>
      update(current, {
        selectedDate: {
          $set: selectedDate,
        },
      })
    );
    setCancelRequestedDate(selectedDate);
  }, [selectedDate, setCancelRequestedDate]);
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [showSummerPauseAndDiscount, setShowSummerPauseAndDiscount] = useState(false);
  const [cancelEnrolmentPayload, setCancelEnrolmentPayload] = useState(null);
  const handleCancelEnrolment = (onSubmitSuccess: () => void, onSubmitFailed: () => void) => {
    dispatch(
      cancelEnrolment({
        rating: cancelSelection.rating,
        enrolmentId: enrolmentSfid,
        selectedReasons: cancelSelection.selectedReasons,
        otherMessage: cancelSelection.other.message,
        message: cancelSelection.message,
        endDate: selectedDate,
        onSubmitSuccess,
        onSubmitFailed,
      })
    );
  };

  // Change here
  const onClickHandler = () => {
    if (shouldOfferSummerPauseAndDiscount(cancelSelection.selectedReasons) && !hasTakenSummerOffer) {
      setCancelEnrolmentPayload({
        rating: cancelSelection.rating,
        enrolmentId: enrolmentSfid,
        selectedReasons: cancelSelection.selectedReasons,
        otherMessage: cancelSelection.other.message,
        message: cancelSelection.message,
        endDate: selectedDate,
        onSubmitSuccess: submitSuccessCallback,
      });
      setShowSummerPauseAndDiscount(true);
    } else if (
      shouldSuggestChangePlan(
        cancelSelection.selectedReasons,
        accountPricingPlan?.currentPricingPlan,
        accountPricingPlan?.upcomingPricingPlan
      )
    ) {
      setCancelEnrolmentPayload({
        rating: cancelSelection.rating,
        enrolmentId: enrolmentSfid,
        selectedReasons: cancelSelection.selectedReasons,
        otherMessage: cancelSelection.other.message,
        message: cancelSelection.message,
        endDate: selectedDate,
        onSubmitSuccess: submitSuccessCallback,
      });
      setShowChangePlan(true);
    } else {
      dispatch(
        cancelEnrolment({
          rating: cancelSelection.rating,
          enrolmentId: enrolmentSfid,
          selectedReasons: cancelSelection.selectedReasons,
          otherMessage: cancelSelection.other.message,
          message: cancelSelection.message,
          endDate: selectedDate,
          onSubmitSuccess: submitSuccessCallback,
        })
      );
    }
  };

  const onGoBack = () => {
    if (ref?.current?.getStepIndex() > 0) {
      ref.current.goBack();
      window.scrollTo(0, document.body.scrollHeight);
    } else {
      goBack();
    }
  };

  if (showChangePlan) {
    return (
      <ChangePricingPlanPage
        interrupting
        cancelEnrolmentPayload={cancelEnrolmentPayload}
        onGoBack={() => setShowChangePlan(false)}
      />
    );
  }

  if (showSummerPauseAndDiscount) {
    return (
      <SummerPauseAndDiscount selectedEnrolment={selectedEnrolment} handleCancelEnrolment={handleCancelEnrolment} />
    );
  }

  return (
    <CardLayout
      className="poppins !rounded-lg"
      customGoBack={onGoBack}
      banner={ref?.current?.getStepIndex() <= 0 && <CancellationDeterrent />}
    >
      <div className="lg:px-5 lg:pt-5" id="cancel-enrolment-form">
        {submitError && errorMsg && <ErrorBlock errorMsg={errorMsg} />}
        {enrolmentPolicyError && (
          <ErrorBlock errorMsg="There was an error encountered while retrieving the pause policy." />
        )}
        {salesforceCasesQueryError && (
          <ErrorBlock errorMsg="There was an error encountered while retrieving some information." />
        )}
        <div className="flex flex-col gap-y-5 pb-8">
          <FormSubHeading className="merriweather-important !text-2xl lg:!text-[32px]" />
          <p className="text-lg text-grey-5">
            We're sorry to see you go. We need some information from you to process your request.
          </p>
          <p className="flex flex-col gap-y-2 rounded-lg bg-[#EFE9E1] p-4">
            <span className="text-lg font-semibold">
              {capitalize(student)}'s {subject} enrolment
            </span>
            {!isEmpty(selectedEnrolment.sessions) && (
              <span className="text-base">
                {nextSessionIntro} <span className="font-semibold">{nextSessionWhenAndWhere}</span>
              </span>
            )}
          </p>
        </div>
        <form>
          <div className="mb-3 max-w-md">
            <DatePicker
              {...datePickerProps}
              icon={<img src={CalendarIcon} alt="calendar icon" width={24} height={24} />}
            />
          </div>
          <CancelEnrolmentAlert selectedDate={selectedDate} selectedEnrolment={selectedEnrolment} />
          <CancellationSteps
            ref={ref}
            onSubmitCancelRequest={onClickHandler}
            cancelSelection={cancelSelection}
            setCancelSelection={setCancelSelection}
            selectedEnrolment={selectedEnrolment}
          />
        </form>
      </div>
    </CardLayout>
  );
};

export default FormCancelEnrolment;
